import React, { useCallback, useMemo, useRef } from "react";
import { languages } from "src/utils/localization/languages/languages";

import * as styles from "./TopNavLanguageSelector.module.scss";
import { AlternateLink } from "src/utils/localization/alternateLinks";
import { Language } from "src/utils/localization/languages/types";
import clsx from "clsx";
import { useClickOutside } from "src/utils/useClickOutside";
import TopNavLink from "./TopNavLink";

function urlForLanguage(language: Language, alternateLinks: AlternateLink[]) {
  if (!alternateLinks) return `/${language.path}`;
  const altLink = alternateLinks.find((l) => l.ogLangCode == language.ogLang);
  if (altLink) return altLink.path;

  return null;
}

type Props = {
  currentLanguage: Language;
  alternateLinks: AlternateLink[];
};

export const TopNavLanguageSelector: React.FC<
  React.PropsWithChildren<Props>
> = ({ currentLanguage, alternateLinks }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const onExpandToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  useClickOutside({
    enable: isExpanded,
    closeFunction: onExpandToggle,
    containerElement: containerRef.current?.parentElement
  });

  const list = useMemo(() => {
    const othersLanguages = languages
      .filter((l) => l.code != currentLanguage.code)
      .map((language) => ({
        language: language,
        url: urlForLanguage(language, alternateLinks)
      }))
      .filter((u) => !!u.url);

    return (
      <ul
        role="listbox"
        id="listbox"
        className={styles.languageList}
        data-content-name="language_selector"
      >
        {othersLanguages.map(({ language, url }) => {
          if (!url) return null;
          return (
            <li key={language.code}>
              <a
                href={url}
                lang={language.ogLang}
                hrefLang={language.ogLang}
                role="option"
                aria-selected={false}
                data-content-piece={language.ogLang}
              >
                {language.name}
              </a>
            </li>
          );
        })}
      </ul>
    );
  }, [currentLanguage.code, alternateLinks]);

  return (
    <>
      <TopNavLink
        _key="language-selector"
        renderAs="button"
        role="combobox"
        id="select"
        value="Select"
        aria-controls="listbox"
        aria-haspopup="listbox"
        tabIndex={0}
        aria-expanded={isExpanded ? "true" : "false"}
        className={clsx({
          [styles.expanded]: isExpanded
        })}
        onClick={onExpandToggle}
        iconName="chevron-down"
        title={currentLanguage.shortName}
      ></TopNavLink>
      <div
        className={clsx(styles.listWrapper, { [styles.expanded]: isExpanded })}
        ref={containerRef}
      >
        {list}
      </div>
    </>
  );
};
